import { create } from "zustand";
import { persist } from "zustand/middleware";

// Two-column app chrome. The section rail (column 1) is a fixed icon+label
// list that collapses to icons; the section panel (column 2) holds the active
// section's sub-nav and is user-resizable (drag handle) within [MIN, MAX].
export const SIDEBAR_RAIL_WIDTH = 64;
// Wide enough that the longest label plus its trailing chip ("Workflows" +
// BETA) fits without truncating - the row's truncate is a guard for the odd
// long title, not the resting state.
export const SIDEBAR_SECTION_WIDTH = 224;
export const SIDEBAR_MIN_WIDTH = 160;
export const SIDEBAR_MAX_WIDTH = 320;
export const SIDEBAR_DEFAULT_WIDTH = 208;

const clampWidth = (w: number) =>
  Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, w));

interface SidebarLayoutState {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
  toggleSidebar: () => void;
  // Section rail: collapsed = icon-only. Width is the section panel's, drag-
  // resized in px. Both persist so the user's layout sticks across reloads.
  railCollapsed: boolean;
  setRailCollapsed: (collapsed: boolean) => void;
  toggleRailCollapsed: () => void;
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
  // A page with a pinned footer (PageLayout `footer`) is showing; floating
  // nudges in the bottom corner step aside so they don't cover its actions.
  hasPinnedFooter: boolean;
  setHasPinnedFooter: (hasPinnedFooter: boolean) => void;
}

export const useSidebarLayoutStore = create<SidebarLayoutState>()(
  persist(
    (set) => ({
      isSidebarOpen: true,
      setIsSidebarOpen: (isOpen) => set({ isSidebarOpen: isOpen }),
      toggleSidebar: () =>
        set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
      railCollapsed: true,
      setRailCollapsed: (collapsed) => set({ railCollapsed: collapsed }),
      toggleRailCollapsed: () =>
        set((state) => ({ railCollapsed: !state.railCollapsed })),
      sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
      setSidebarWidth: (width) => set({ sidebarWidth: clampWidth(width) }),
      hasPinnedFooter: false,
      setHasPinnedFooter: (hasPinnedFooter) => set({ hasPinnedFooter }),
    }),
    {
      name: "sidebar-layout",
      version: 4,
      // Each version changes a default; drop the persisted values so the new
      // default applies instead of a stale one sticking forever.
      migrate: () => ({
        railCollapsed: true,
        sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
      }),
      // Only the rail layout is durable; transient panel/expansion state stays
      // in memory so it resets per session.
      partialize: (state) => ({
        railCollapsed: state.railCollapsed,
        sidebarWidth: state.sidebarWidth,
      }),
    },
  ),
);
